import type { CheckList, NumberingDraft, NumberingIn, NumberingRun, RenumberIn, TrialState } from "@api/generated/model";
import {
  numberingPreview,
  numberingRenumberPreview,
  useNumberingChecks,
  useNumberingGroups,
  useNumberingLines,
  useNumberingPlots,
  useNumberingRenumberRun,
  useNumberingRun,
  useNumberingSaveChecks,
  useNumberingTrials,
} from "@api/generated/numbering/numbering";
import { useUserMe } from "@api/generated/user/user";
import { Icon } from "@app/icons";
import { usePreview, useRunResult } from "@app/preview";
import { hasRole, useAutosave, useSeasonFromLink, useSettings } from "@app/workspace";
import { SavedQuerySelect, useSavedQuery } from "@features/queries/SavedQuerySelect";
import {
  CheckChips,
  LayoutGrid,
  ManualOrder,
  ParallelTable,
  PlanTable,
  SchemeCards,
  SequenceChoice,
  trialChoices,
} from "@features/trials/NumberingParts";
import {
  choicesOf,
  isQuery,
  MAX_CHECKS,
  type NumberingSpec,
  nextParallel,
  rangeOf,
  renumberOf,
  renumberStart,
  requestOf,
  specOf,
  type Task,
} from "@features/trials/numbering";
import { num } from "@ui/format";
import { QueryFeedback } from "@ui/QueryFeedback";
import "@features/trials/numbering.css";
import { ResultBanner } from "@features/system/undo";
import { Button, Checkbox, Input, Loader, Modal, NumberInput, Select, Tabs } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AnimatedTabsList } from "@ui/AnimatedTabsList";
import { ChoiceList } from "@ui/ChoiceList";
import { formatNumber, shortTime } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { RunBar } from "@ui/RunBar";
import { type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export const Route = createFileRoute("/season/plot-numbering")({
  validateSearch: z.object({ ev: z.number().int().optional() }),
  component: NumberingPage,
});

const PREVIEW = "numbering-preview"; // + the saved query's version: a query saved again asks for a new plan
const TASKS: Task[] = ["number", "renumber"];

/** Tenyészidőszak → Parcellaszámozás (the legacy Plot Numbering): the temporary plots of the next year's trials
 *  (as the selection wrote them) get their final numbers, with checks and parallel experiments, in the selection's
 *  order, a saved query's or a click order; a numbered trial can be renumbered. The plan is kept with the user's
 *  settings; the numbering, the renumbering and a saved check register are one undoable operation each. */
function NumberingPage() {
  const { t } = useTranslation();
  const canNumber = hasRole(useUserMe().data?.roles, "nemesito");
  const ev = useSeasonFromLink(Route.useSearch().ev);
  const settings = useSettings();
  const trials = useNumberingTrials({ ev: (ev ?? 0) + 1 }, { query: { enabled: !!ev && canNumber } });
  if (!canNumber) {
    return (
      <div className="page">
        <PageHeader title={t("plotNumbering.title")} />
        <p className="mu">{t("plotNumbering.breederOnly")}</p>
      </div>
    );
  }
  if (!settings || !ev || !trials.data) return <Loader size="sm" m="md" />;
  return (
    <NumberingPlan
      key={ev}
      ev={ev + 1}
      trials={trials.data}
      stored={settings.numbering_draft ?? null}
      pgm={settings.default_pgm}
      gh={settings.default_gh}
    />
  );
}

type Done = NumberingRun & { kis: string; task: Task };

function NumberingPlan(props: { ev: number; trials: TrialState[]; stored: NumberingDraft | null; pgm: string; gh: string }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { ev, trials } = props;
  const [init] = useState(() => (props.stored?.ev === ev ? props.stored : null));
  const [kis, setKis] = useState(() => init?.kis ?? trials.find((tr) => tr.temporary > 0)?.kis ?? "");
  const [spec, setSpec] = useState<NumberingSpec>(() => specOf(init));
  const draft = useMemo<NumberingDraft>(() => ({ ev, kis, spec: spec as never }), [ev, kis, spec]);
  const { savedAt } = useAutosave("numbering_draft", draft, init);
  const [done, setDone] = useState<Done | null>(null); // the last run, shown until another trial
  const patch = (p: Partial<NumberingSpec>) => setSpec((s) => ({ ...s, ...p }));
  const choose = (next: string) => {
    setKis(next);
    // numbers, orders and left-out lines belong to the trial; the scheme, checks and parallel experiments carry over
    setSpec((s) => ({
      ...s,
      start: null,
      seq: null,
      order: [],
      renumber: [],
      renumber_start: null,
      parallels: s.parallels.map((p) => ({ ...p, start: null, exclude: [] })),
    }));
  };
  const refresh = () => {
    client.removeQueries({ predicate: (q) => String(q.queryKey[0]).startsWith(PREVIEW) }); // the trial changed
    for (const key of ["/api/v1/numbering/trials", "/api/v1/numbering/lines", "/api/v1/numbering/plots", "measurements"]) {
      void client.invalidateQueries({ queryKey: [key] });
    }
  };
  const trial = trials.find((tr) => tr.kis === kis);
  const head = (
    <>
      <Tabs value={spec.task} onChange={(v) => v && patch({ task: v as Task })}>
        <AnimatedTabsList>
          {TASKS.map((task) => (
            <Tabs.Tab key={task} value={task}>
              {t(`plotNumbering.task.${task}`)}
            </Tabs.Tab>
          ))}
        </AnimatedTabsList>
      </Tabs>
      {done?.kis === kis && done.task === spec.task && (
        <ResultBanner operation={done.operation}>
          <b>
            {t(done.task === "number" ? "plotNumbering.done" : "plotNumbering.renumberDone", { count: formatNumber(done.written), kis })}
          </b>
        </ResultBanner>
      )}
    </>
  );
  const note = (text: string) => (
    <Main head={head}>
      <section className="card pad">
        <span className="sm mu">{text}</span>
      </section>
    </Main>
  );

  return (
    <div className="page fill">
      <PageHeader
        title={t("plotNumbering.title")}
        season
        subtitle={t("plotNumbering.subtitle")}
        actions={savedAt && <span className="sm mu">{t("plotNumbering.draftSaved", { time: shortTime(savedAt.toISOString()) })}</span>}
      />
      <div className="fill-row">
        <ChoiceList
          title={t("plotNumbering.trials", { ev })}
          items={trialChoices(t, trials, kis)}
          value={kis}
          onChange={(id) => choose(String(id))}
          empty={t("plotNumbering.noTrials", { ev })}
        />
        {!trial ? (
          note(t("plotNumbering.chooseTrial"))
        ) : spec.task === "renumber" ? (
          trial.first == null ? (
            note(t("plotNumbering.renumberNone", { kis }))
          ) : (
            <Renumbering
              head={head}
              ev={ev}
              kis={kis}
              spec={spec}
              patch={patch}
              onDone={(out) => {
                setDone({ ...out, kis, task: "renumber" });
                patch({ renumber: [], renumber_start: null });
                refresh();
              }}
            />
          )
        ) : !trial.temporary ? (
          note(t("plotNumbering.noTemporaryPlots", { kis }))
        ) : (
          <Numbering
            head={head}
            ev={ev}
            kis={kis}
            spec={spec}
            patch={patch}
            defaults={{ pgm: props.pgm, gh: props.gh }}
            onDone={(out) => {
              setDone({ ...out, kis, task: "number" });
              patch({ start: null, seq: null, order: [] });
              refresh(); // the new trial state asks for a fresh preview
            }}
          />
        )}
      </div>
    </div>
  );
}

/** The page's middle column: the task tabs and the last run's result, then the task's cards. */
function Main(props: { head: ReactNode; children: ReactNode }) {
  return (
    <div className="grow stack nb-main">
      {props.head}
      {props.children}
    </div>
  );
}

type Part = {
  head: ReactNode;
  ev: number;
  kis: string;
  spec: NumberingSpec;
  patch: (p: Partial<NumberingSpec>) => void;
  onDone: (out: NumberingRun) => void;
};

/** Numbering a trial's temporary plots: scheme, checks, order, parallel experiments, the plan and the run. */
function Numbering(props: Part & { defaults: { pgm: string; gh: string } }) {
  const { t } = useTranslation();
  const { ev, kis, spec, patch } = props;
  const [table, setTable] = useState(false);
  const { scheme, sequence } = spec;
  const pgm = spec.pgm ?? props.defaults.pgm;
  const gh = spec.gh ?? props.defaults.gh;
  const lines = useNumberingLines({ ev, kis }, { query: { enabled: !!kis } }).data ?? [];
  const groups = useNumberingGroups({ ev }, { query: { enabled: scheme !== "normal", staleTime: 300_000 } }).data;
  const register = useNumberingChecks(
    { ev, scheme: scheme === "c" ? "c" : "b", pgm, gh },
    { query: { enabled: scheme !== "normal", staleTime: 300_000 } },
  ).data;
  const saved = useSavedQuery(spec.query, isQuery(sequence));
  const chosenQuery = saved.query;

  const body = useMemo(() => requestOf(ev, kis, spec), [ev, kis, spec]);
  const preview = usePreview(`${PREVIEW}:${saved.stamp}`, saved.ready ? body : null, (b, signal) =>
    numberingPreview(b as NumberingIn, { signal }),
  );
  const plan = body ? preview.data : undefined;
  const run = useNumberingRun({ mutation: { onSuccess: props.onDone } });

  const plots = plan?.plots ?? [];
  const range = rangeOf(plots);
  const firsts = Object.fromEntries((plan?.parallels ?? []).map((p) => [p.kis, p.first]));
  const parallels = (plan?.parallels ?? []).filter((p) => p.plots).map((p) => `${p.kis} ${p.first}–${p.last}`);
  const notes = [
    ...(plan?.skipped.length ? [t("plotNumbering.inUseKept", { count: plan.skipped.length })] : []),
    ...(plan?.left ? [t("plotNumbering.notInSequence", { count: plan.left })] : []),
  ];

  return (
    <>
      <Main head={props.head}>
        <QueryFeedback busy={preview.busy} error={preview.error} retry={preview.refetch} label={t("async.preview")} />
        <section className="card pad">
          <h2 className="h2">{t("plotNumbering.modeTitle", { kis })}</h2>
          <SchemeCards scheme={scheme} kis={kis} onChange={(s) => patch({ scheme: s, start: null, seq: null })} />
          <div className="nb-fields">
            {scheme !== "normal" && (
              <NumberInput
                w={120}
                size="xs"
                label={t(scheme === "c" ? "plotNumbering.plotsPerTrial" : "plotNumbering.frequency")}
                min={2}
                max={1000}
                allowDecimal={false}
                value={spec.freq}
                onChange={(v) => typeof v === "number" && patch({ freq: v })}
              />
            )}
            {scheme === "c" && (
              <NumberInput
                w={120}
                size="xs"
                label={t("plotNumbering.firstTrial")}
                min={1}
                allowDecimal={false}
                prefix={kis}
                placeholder={plan ? `${kis}${plan.seq}` : kis}
                value={spec.seq ?? ""}
                onChange={(v) => patch({ seq: num(v) })}
              />
            )}
            <NumberInput
              w={120}
              size="xs"
              label={t("plotNumbering.startNumber")}
              min={1}
              allowDecimal={false}
              placeholder={plan ? String(plan.start) : ""}
              value={spec.start ?? ""}
              onChange={(v) => patch({ start: num(v) })}
            />
            <Checkbox
              size="xs"
              label={t("plotNumbering.newSid")}
              description={t("plotNumbering.newSidHelp")}
              checked={spec.new_sid}
              onChange={(e) => patch({ new_sid: e.currentTarget.checked })}
            />
          </div>
          {scheme !== "normal" && (
            <div className="nb-fields">
              <Select
                w={90}
                size="xs"
                label={t("plotNumbering.pgm")}
                data={choicesOf(groups?.pgm, pgm, props.defaults.pgm)}
                value={pgm}
                allowDeselect={false}
                onChange={(v) => v && patch({ pgm: v })}
              />
              <Select
                w={90}
                size="xs"
                label={t("plotNumbering.growthHabit")}
                data={choicesOf(groups?.gh, gh, props.defaults.gh)}
                value={gh}
                allowDeselect={false}
                onChange={(v) => v && patch({ gh: v })}
              />
              <Input.Wrapper size="xs" label={t("plotNumbering.standards", { count: MAX_CHECKS })} className="grow">
                <RegisterChips ev={ev} spec={spec} pgm={pgm} gh={gh} register={register} patch={patch} />
              </Input.Wrapper>
            </div>
          )}
          <div className="nb-fields">
            <SequenceChoice sequence={sequence} ev={ev - 1} kis={kis} onChange={(s) => patch({ sequence: s })} />
            {isQuery(sequence) && <SavedQuerySelect value={spec.query} onChange={(id) => patch({ query: id })} w={260} required />}
          </div>
          {isQuery(sequence) && plan?.query_rows != null && (
            <span className={plan.query_truncated ? "sm note warn" : "sm mu"}>
              {plan.query_truncated
                ? t("plotNumbering.queryTruncated", { count: formatNumber(plan.query_rows) })
                : t("plotNumbering.queryResult", { count: plan.lines, rows: formatNumber(plan.query_rows), skipped: plan.left })}
            </span>
          )}
        </section>

        {sequence === "manual" && <ManualOrder lines={lines} order={spec.order} onChange={(order) => patch({ order })} />}

        <section className="card pad">
          <div className="hstack" style={{ gap: 10 }}>
            <h2 className="h2 grow">{t("plotNumbering.parallelTrials")}</h2>
            <Button
              size="compact-xs"
              variant="default"
              leftSection={<Icon name="plus" size={12} />}
              onClick={() => patch({ parallels: [...spec.parallels, nextParallel(spec.parallels)] })}
            >
              {t("plotNumbering.addParallel")}
            </Button>
          </div>
          {spec.parallels.length ? (
            <>
              <ParallelTable parallels={spec.parallels} lines={lines} firsts={firsts} onChange={(p) => patch({ parallels: p })} />
              <Checkbox
                className="note warn"
                size="xs"
                checked={spec.parallel_main_line}
                onChange={(e) => patch({ parallel_main_line: e.currentTarget.checked })}
                label={
                  <>
                    {t("plotNumbering.parallelMainRef")} <span className="mu">{t("plotNumbering.parallelMainRefHelp")}</span>
                  </>
                }
              />
            </>
          ) : (
            <span className="sm mu">{t("plotNumbering.noParallel", { kis })}</span>
          )}
          <span className="sm mu">
            {t("plotNumbering.parallelLater")} <Link to="/season/parallel-experiments">{t("nav.season.parallelExperiments")}</Link>
          </span>
        </section>

        <RunBar
          requirements={[
            { label: t("plotNumbering.needStandard"), done: scheme === "normal" || spec.checks.length > 0 },
            { label: t("plotNumbering.needQuery"), done: !isQuery(sequence) || !!chosenQuery },
            { label: t("queries.source.needsAllRows"), done: !plan?.query_truncated },
            { label: t("plotNumbering.needSequence"), done: sequence !== "manual" || spec.order.length > 0 },
            { label: t("plotNumbering.needFreeNumbers", { list: plan?.taken.slice(0, 8).join(", ") }), done: !plan?.taken.length },
            { label: t("plotNumbering.needPlot"), done: !plan || plots.length > 0 },
          ]}
          summary={
            plan && (
              <span>
                <b>{t("plotNumbering.summary", { line: plan.lines, standard: plan.checks, total: plots.length, range: range })}</b>
                {parallels.length > 0 && t("plotNumbering.summaryParallel", { parallel: parallels.join(", ") })}
                {notes.length > 0 && <span className="mu"> · {notes.join(" · ")}</span>}
              </span>
            )
          }
          undoable
          busy={preview.busy || !plan}
          run={{
            label: t("plotNumbering.run", { count: plots.length }),
            loading: run.isPending,
            onClick: () => body && plan && run.mutate({ data: { ...body, token: plan.token } }),
          }}
        >
          <Button variant="default" disabled={!plots.length} onClick={() => setTable(true)}>
            {t("plotNumbering.previewTable")}
          </Button>
        </RunBar>
      </Main>
      <LayoutGrid plots={plots} />
      <Modal opened={table} onClose={() => setTable(false)} title={t("plotNumbering.previewTable")} size="xl">
        <PlanTable plots={plots} />
      </Modal>
    </>
  );
}

/** The checks with the register of the chosen PGM and growth habit: load it, or save the checks and the frequency as
 *  it (the legacy Add Standard / remove a standard; one undoable operation). */
function RegisterChips(props: {
  ev: number;
  spec: NumberingSpec;
  pgm: string;
  gh: string;
  register: CheckList | undefined;
  patch: (p: Partial<NumberingSpec>) => void;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const { spec, register, patch } = props;
  const scheme = spec.scheme === "c" ? "c" : "b";
  const request = register && { ev: props.ev, scheme, pgm: props.pgm, gh: props.gh, freq: spec.freq, checks: spec.checks };
  const { result, keep } = useRunResult<NumberingRun>(request);
  const same =
    !!register &&
    register.freq === spec.freq &&
    register.checks.map((c) => `${c.kis}|${c.parc}`).join() === spec.checks.map((c) => `${c.kis}|${c.parc}`).join();
  const save = useNumberingSaveChecks({
    mutation: {
      onSuccess: (out) => {
        keep(out);
        void client.invalidateQueries({ queryKey: ["/api/v1/numbering/checks"] });
      },
    },
  });
  const onSave =
    register && !same && spec.freq >= 1 && (spec.checks.length > 0 || register.checks.length > 0)
      ? () =>
          save.mutate({
            data: {
              ev: props.ev,
              scheme,
              pgm: props.pgm,
              gh: props.gh,
              freq: spec.freq,
              checks: spec.checks.map((c) => ({ kis: c.kis, parc: c.parc })),
              token: register.token,
            },
          })
      : undefined;
  return (
    <>
      <CheckChips
        ev={props.ev - 1}
        pgm={props.pgm}
        gh={props.gh}
        checks={spec.checks}
        register={register}
        onChange={(checks) => patch({ checks })}
        onLoad={(c) => patch({ checks: c.checks, freq: c.freq ?? spec.freq })}
        onSave={onSave}
        saving={save.isPending}
      />
      {result && register && (
        <ResultBanner operation={result.operation}>
          {t("plotNumbering.standardListSaved", { name: register.trial, count: spec.checks.length, freq: spec.freq })}
        </ResultBanner>
      )}
    </>
  );
}

/** Renumbering a numbered trial (the legacy on-screen numbering of numbered plots): the plots clicked get the numbers
 *  from a start, with their measurements, ledger rows and every pointer to them. */
function Renumbering(props: Part) {
  const { t } = useTranslation();
  const { ev, kis, spec, patch } = props;
  const plots = useNumberingPlots({ ev, kis }).data ?? [];
  const body = useMemo(() => renumberOf(ev, kis, spec), [ev, kis, spec]);
  const preview = usePreview(`${PREVIEW}:renumber`, body, (b, signal) => numberingRenumberPreview(b as RenumberIn, { signal }));
  const plan = body ? preview.data : undefined;
  const run = useNumberingRenumberRun({ mutation: { onSuccess: props.onDone } });
  const blocked = (plan?.blocked ?? []).map((b) => `${b.parc} (${b.uses.map((u) => t(`plotNumbering.uses.${u}`)).join(", ")})`);
  return (
    <Main head={props.head}>
      <QueryFeedback busy={preview.busy} error={preview.error} retry={preview.refetch} label={t("async.preview")} />
      <ManualOrder
        lines={plots}
        order={spec.renumber}
        onChange={(renumber) => patch({ renumber })}
        start={spec.renumber.length ? renumberStart(spec) : 1}
        actions={
          <NumberInput
            w={130}
            size="xs"
            aria-label={t("plotNumbering.renumberStart")}
            placeholder={spec.renumber.length ? String(renumberStart(spec)) : t("plotNumbering.renumberStart")}
            min={1}
            allowDecimal={false}
            value={spec.renumber_start ?? ""}
            onChange={(v) => patch({ renumber_start: num(v) })}
          />
        }
      />
      <RunBar
        requirements={[
          { label: t("plotNumbering.needRenumberSequence"), done: spec.renumber.length > 0 },
          { label: t("plotNumbering.needFreeNumbers", { list: plan?.taken.slice(0, 8).join(", ") }), done: !plan?.taken.length },
          { label: t("plotNumbering.needUnreferenced", { list: blocked.slice(0, 5).join(", ") }), done: !blocked.length },
          { label: t("plotNumbering.needChange"), done: !plan || plan.moves.length > 0 },
        ]}
        summary={
          plan && (
            <span>
              <b>{t("plotNumbering.renumberSummary", { count: plan.moves.length })}</b>
              {plan.kept > 0 && <span className="mu"> · {t("plotNumbering.renumberUnchanged", { count: plan.kept })}</span>}
            </span>
          )
        }
        undoable
        busy={preview.busy || !plan}
        run={{
          label: t("plotNumbering.renumberRun", { count: plan?.moves.length ?? 0 }),
          loading: run.isPending,
          onClick: () => body && plan && run.mutate({ data: { ...body, token: plan.token } }),
        }}
      />
    </Main>
  );
}
